import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';

@Component({
    selector: 'fi-circular-gradient-progress-bar',
    template: `
        <div class="main-circular-gradient-progress-bar" [class.full-filled]="value === 100">
            <div class="gradient-progress-cover" #gradientProgressCoverElementRef>
                <div class="gradient-progress-cover-inner-circle" #gradientProgressCoverInnerCircleElementRef></div>
            </div>
            <span class="edge top"></span>
            <span class="edge bottom"></span>
            <div class="progress-value-label">{{ value }}%</div>
        </div>
    `,
    styleUrls: ['fi-circular-gradient-progress-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-circular-gradient-progress-bar' },
    standalone: false,
})
export class FiCircularGradientProgressBarComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChild('gradientProgressCoverElementRef', { static: true })
    gradientProgressCoverElementRef: ElementRef<HTMLElement>;
    @ViewChild('gradientProgressCoverInnerCircleElementRef', { static: true })
    gradientProgressCoverInnerCircleElementRef: ElementRef<HTMLElement>;

    @Input() backgroundColor: string = '#fff';
    @Input() innerCircleColor: string = '#e5eff6';
    @Input() progressGradientNotFilled: string = 'linear-gradient(90deg, #FFDCC4 50%, #FF8E1C 100%)';
    @Input() progressGradientFilled: string = 'linear-gradient(90deg, #99FFA3 50%, #13CA25 100%)';
    @Input() value: number;

    private readonly degreesMultiplier: number = 3.6;
    private readonly gradientAnimationMS: number = 10;
    private readonly progressAttributeName: string = 'progress';

    private progressBarAnimationInterval: NodeJS.Timeout;
    private tempProgressBarValue: number;

    // note: same must be in styles file also
    private readonly gradientProgressBarNotFilledStylePropertyName: string =
        '--circular-gradient-progress-bar-not-filled';
    private readonly gradientProgressBarFilledStylePropertyName: string = '--circular-gradient-progress-bar-filled';

    constructor(
        private renderer: Renderer2,
        private elementRef: ElementRef,
    ) {}

    ngOnInit() {
        this.calculateProgressBar();
        this.setGradientColors();
    }

    private setGradientColors() {
        this.elementRef.nativeElement.style.setProperty(
            this.gradientProgressBarNotFilledStylePropertyName,
            this.progressGradientNotFilled,
        );
        this.elementRef.nativeElement.style.setProperty(
            this.gradientProgressBarFilledStylePropertyName,
            this.progressGradientFilled,
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.value) {
            this.calculateProgressBar();
        }
    }

    private calculateProgressBar() {
        if (this.progressBarAnimationInterval) {
            clearInterval(this.progressBarAnimationInterval);
            this.progressBarAnimationInterval = undefined;
            this.gradientProgressCoverElementRef.nativeElement.setAttribute(
                this.progressAttributeName,
                this.tempProgressBarValue.toString(),
            );
        }

        const oldProgressValue: number =
            +this.gradientProgressCoverElementRef.nativeElement.getAttribute(this.progressAttributeName) | 0;
        this.tempProgressBarValue = oldProgressValue;

        if (this.tempProgressBarValue === this.value) return;

        this.progressBarAnimationInterval = setInterval(() => {
            this.tempProgressBarValue =
                this.value > oldProgressValue ? this.tempProgressBarValue + 1 : this.tempProgressBarValue - 1;
            this.setProgressGradient(this.tempProgressBarValue);
            if (this.tempProgressBarValue === this.value) {
                this.gradientProgressCoverElementRef.nativeElement.setAttribute(
                    this.progressAttributeName,
                    this.tempProgressBarValue.toString(),
                );
                clearInterval(this.progressBarAnimationInterval);
            }
        }, this.gradientAnimationMS);
    }

    private setProgressGradient(value: number) {
        this.renderer.setStyle(
            this.gradientProgressCoverElementRef.nativeElement,
            'background',
            `conic-gradient(transparent ${value * this.degreesMultiplier}deg, ${this.backgroundColor} 0deg)`,
        );
        this.renderer.setStyle(
            this.gradientProgressCoverInnerCircleElementRef.nativeElement,
            'background',
            `conic-gradient(transparent ${value * this.degreesMultiplier}deg, ${this.innerCircleColor} 0deg)`,
        );
    }

    ngOnDestroy() {
        if (this.progressBarAnimationInterval) {
            clearInterval(this.progressBarAnimationInterval);
            this.progressBarAnimationInterval = undefined;
        }
    }
}
